import * as React from 'react'
import { datalayerHandler } from '../../../utils/datalayer'
import { useInView } from '../../../utils/hooks/hooks/useInView'
import { Image, ImageBase, ImageOptions } from '../../../utils/images'
import { Link } from '../../../utils/links'
import { maybeToMailPlusLink } from '../../../utils/mailplus'
import { textImageBlockClasses } from '../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode } from '../../../utils/utils'
import { ActionProps, InviewProps } from './blocksState'

export type TextImageBlock = {
  index: number
  imageLocation: 'left' | 'right'
  image: ImageBase & ImageOptions
  label?: string
  title?: React.ReactNode
  text: string
  meta: string
  primaryAction?: ActionProps
  secondaryAction?: ActionProps
  inview?: InviewProps
}

export type TextImageBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  contentClassName: string
  headerClassName: string
  labelClassName: string
  titleClassName: string
  textClassName: string
  introClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickablePrimaryClassName: string
  clickableSecondaryClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  figureClassName: string
  imageClassName: string
  imageNonExpandableClassName: string
  pictureClassName: string
  iconPrimaryId: string
  iconSecondaryId: string
}>

export const TextImageBlockLayout = (
  props: TextImageBlock
) => {
  const {
    inview,
    index,
    image,
    imageLocation,
    label,
    primaryAction,
    secondaryAction,
    text,
    title,
    meta
  } = props

  const classes = textImageBlockClasses(imageLocation)
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("AdvertisementBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')

  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(classes, 'innerClassName', 'py-5 row')}>
        <div
          className={getClassName(
            classes,
            'contentClassName',
            `col-md-6 mb-4 order-${imageLocation == 'left' ? 2 : 1}`
          )}
        >
          {(title || label) && (
            <header className={getClassName(classes, 'headerClassName', '')}>
              {label && (
                <span
                  className={getClassName(
                    classes,
                    'labelClassName',
                    'd-block small'
                  )}
                >
                  {label}
                </span>
              )}
              {title && (
                <h2
                  className={getClassName(classes, 'titleClassName', 'mb-3')}
                  id={`${index}`}
                >
                  {title}
                </h2>
              )}
            </header>
          )}

          {text && (
            <div className={getClassName(classes, 'textClassName', '')}>
              <div
                className={getClassName(classes, 'introClassName', '')}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            </div>
          )}

          {/* [not CUSTOM & HARCODED anymore */}
          {meta && (
            <div className='block__meta text--xs'>
              <p>{meta}</p>
            </div>
          )}

          {(primaryAction || secondaryAction) && (
            <footer className={getClassName(classes, 'footerClassName', '')}>
              {primaryAction && primaryAction.title && (
                <div
                  className={getClassName(
                    classes,
                    'clickableWrapperClassName',
                    'd-inline-block'
                  )}
                >
                  <Link
                    url={primaryAction.url}
                    title={primaryAction.title}
                    className={
                      getClassName(
                        classes,
                        'clickablePrimaryClassName',
                        'btn btn-dark mr-2 d-inline-flex'
                      )
                    }
                    onClick={() =>
                      datalayerHandler('text-image', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: maybeToMailPlusLink(primaryAction.url),
                        label: primaryAction.title,
                      })
                    }
                  >
                    <span
                      className={getClassName(
                        classes,
                        'clickableLabelClassName',
                        ''
                      )}
                    >
                      {htmlDecode(primaryAction.title)}
                    </span>
                    {primaryAction.SVGIcon && (
                      <svg
                        className={getClassName(
                          classes,
                          'clickableIconClassName',
                          'ml-3'
                        )}
                        aria-hidden="true"
                        width={
                          primaryAction.SVGIcon.width
                            ? primaryAction.SVGIcon.width
                            : '24px'
                        }
                        height={
                          primaryAction.SVGIcon.height
                            ? primaryAction.SVGIcon.height
                            : '24px'
                        }
                      >
                        <use
                          xlinkHref={getClassName(
                            classes,
                            'iconSecondaryId',
                            '#icon--chevron-right-large'
                          )}
                        />
                      </svg>
                    )}
                  </Link>
                </div>
              )}

              {secondaryAction && secondaryAction.title && (
                <div
                  className={getClassName(
                    classes,
                    'clickableWrapperClassName',
                    'd-inline-block'
                  )}
                >
                  <Link
                    url={secondaryAction.url}
                    title={secondaryAction.title}
                    className={getClassName(
                      classes,
                      'clickableSecondaryClassName',
                      'btn btn-link px-0 mr-2 d-inline-flex'
                    )}
                    onClick={() =>
                      datalayerHandler('text-image', undefined, undefined, {
                        kind: 'link',
                        title: convertReactNodeToString(title),
                        link: maybeToMailPlusLink(secondaryAction.url),
                        label: secondaryAction.title,
                      })
                    }
                  >
                    <span
                      className={getClassName(
                        classes,
                        'clickableLabelClassName',
                        ''
                      )}
                    >
                      {htmlDecode(secondaryAction.title)}
                    </span>
                    {secondaryAction.SVGIcon && (
                      <svg
                        className={getClassName(
                          classes,
                          'clickableIconClassName',
                          'ml-3'
                        )}
                        aria-hidden="true"
                        width={
                          secondaryAction.SVGIcon.width
                            ? secondaryAction.SVGIcon.width
                            : '24px'
                        }
                        height={
                          secondaryAction.SVGIcon.height
                            ? secondaryAction.SVGIcon.height
                            : '24px'
                        }
                      >
                        <use
                          xlinkHref={getClassName(
                            classes,
                            'iconSecondaryId',
                            '#icon--chevron-right-large'
                          )}
                        />
                      </svg>
                    )}
                  </Link>
                </div>
              )}
            </footer>
          )}
        </div>

        {image && (
          <div
            className={getClassName(
              classes,
              'figureClassName',
              `col-md-6 mb-4 order-${imageLocation == 'left' ? 1 : 2}`
            )}
          >
            <Image
              {...image}
              classNames={{
                image: getClassName(classes, (image?.keepOriginalSize ? 'imageNonExpandableClassName' : 'imageClassName'), 'w-100'),
                picture: getClassName(classes, 'pictureClassName', ''),
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}
